import React from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft } from 'react-icons/fa';
import { theme } from '../../styles/theme';

const PageHeader = styled.div`
  background: linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)),
              url('/images/blog-bg.jpg') no-repeat center center;
  background-size: cover;
  height: 40vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 24px;
  margin-top: 80px;
`;

const PageTitle = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 3rem;
  text-align: center;
  max-width: 1200px;
  width: 100%;

  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const ArticleSection = styled.section`
  padding: 100px 0;
  background: ${theme.colors.primary};
`;

const ArticleWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 24px;
`;

const ArticleContent = styled.div`
  background: ${theme.colors.background.card};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.card};
  border: 1px solid ${theme.colors.border.gold};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.secondary};
  margin-bottom: 24px;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const BackIcon = styled(FaArrowLeft)`
  margin-right: 8px;
`;

const ArticleMeta = styled.div`
  font-size: 0.9rem;
  color: ${theme.colors.text.muted};
  margin-bottom: 24px;
`;

const ArticleHeading = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  color: ${theme.colors.secondary};
  margin-bottom: 16px;
`;

const Paragraph = styled.p`
  font-size: ${theme.typography.fontSize.md};
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing.md};
`;

const Subheading = styled.h3`
  font-size: 1.4rem;
  color: ${theme.colors.secondary};
  margin: 32px 0 16px;
  font-weight: ${theme.typography.fontWeight.semiBold};
  letter-spacing: ${theme.typography.letterSpacing.wide};
  text-transform: uppercase;
`;

const List = styled.ul`
  margin-left: 1.2rem;
  margin-bottom: 16px;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
  line-height: 1.6;
`;

const Emphasis = styled.span`
  font-weight: 600;
`;

const InlineLink = styled.a`
  color: ${theme.colors.accent};
  font-weight: ${theme.typography.fontWeight.semiBold};
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;

  &:hover {
    color: ${theme.colors.secondary};
  }
`;

const HighlightLinkButton = styled.a`
  display: inline-block;
  margin-top: ${theme.spacing.md};
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.pill};
  background: ${theme.colors.gradient.button};
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.typography.fontWeight.semiBold};
  text-decoration: none;
  letter-spacing: ${theme.typography.letterSpacing.wide};
  box-shadow: ${theme.shadows.button};

  &:hover {
    box-shadow: ${theme.shadows.buttonHover};
    transform: translateY(-1px);
  }
`;

const BlogPost = () => {
  const { id } = useParams();

  // DataFind article (id: 1)
  const dataFindContent = (
    <>
      <ArticleMeta>
        May 15, 2025 · Afrotech Group · Product Launch
      </ArticleMeta>

      <ArticleHeading>
        The New Crime-Fighting Platform That Is Spreading Fast: DataFind
      </ArticleHeading>

      <Paragraph>
        South Africa has always been a nation of resilience, but when it comes to theft, many people
        still feel powerless. Cars go missing. Laptops vanish. Tools disappear. Devices are stolen. In
        many cases, owners never see their belongings again.
      </Paragraph>

      <Paragraph>
        A new platform is quietly changing that reality.
      </Paragraph>

      <Paragraph>
        It is simple. It is free. And it is spreading fast.
      </Paragraph>

      <Paragraph>
        <Emphasis>DataFind</Emphasis> is a serial-number-based security tool that helps South Africans
        recover stolen items and protect what they have worked hard for. The platform is designed and
        developed by <Emphasis>Afrotech Group</Emphasis>.
      </Paragraph>

      <Subheading>What Is DataFind?</Subheading>

      <Paragraph>
        DataFind is an online platform that allows you to register your valuables using their serial
        numbers and mark them as stolen if anything ever goes missing. In doing so, it turns serial
        numbers into a powerful community safety tool.
      </Paragraph>

      <Paragraph>Once your items are registered:</Paragraph>

      <List>
        <ListItem>Anyone can check a serial number.</ListItem>
        <ListItem>Stolen items are instantly flagged.</ListItem>
        <ListItem>You can be notified if your item is found.</ListItem>
        <ListItem>Your personal details remain private.</ListItem>
      </List>

      <Paragraph>
        From cars to phones, laptops, security equipment, cameras, power tools, and appliances, if an
        item has a serial number, it can be protected through DataFind.
      </Paragraph>

      <Subheading>Why DataFind Is Spreading So Fast</Subheading>

      <Paragraph>
        <Emphasis>1. Crime is rising but recovery rates remain low</Emphasis>
        <br />
        Many stolen goods are never recovered because there is no central place to verify ownership.
        DataFind fills that gap.
      </Paragraph>

      <Paragraph>
        <Emphasis>2. It is 100% free for the public</Emphasis>
        <br />
        DataFind is not a premium app or paid subscription. It is an open, accessible tool that anyone
        can use.
      </Paragraph>

      <Paragraph>
        <Emphasis>3. It is community-driven</Emphasis>
        <br />
        Mechanics, pawn shops, security companies, technicians, and buyers can all check a serial
        number before accepting an item.
      </Paragraph>

      <Paragraph>
        <Emphasis>4. It makes selling stolen goods difficult</Emphasis>
        <br />
        When second-hand buyers and businesses check serial numbers, thieves lose the ability to sell
        stolen items quickly.
      </Paragraph>

      <Paragraph>
        <Emphasis>5. People are tired of being victims</Emphasis>
        <br />
        For many users, DataFind represents something deeper: a way to fight back and reclaim control
        over their security.
      </Paragraph>

      <Subheading>How DataFind Works</Subheading>

      <Paragraph>DataFind is built to be simple and practical:</Paragraph>

      <Paragraph>
        <Emphasis>Step 1: Register your items</Emphasis>
        <br />
        Go to{' '}
        <InlineLink
          href="https://www.datafind.co.za"
          target="_blank"
          rel="noopener noreferrer"
        >
          www.datafind.co.za
        </InlineLink>{' '}
        and add your belongings by serial number.
      </Paragraph>

      <Paragraph>
        <Emphasis>Step 2: Flag stolen items</Emphasis>
        <br />
        If something goes missing, you can mark it as stolen instantly.
      </Paragraph>

      <Paragraph>
        <Emphasis>Step 3: Anyone can verify a serial number</Emphasis>
        <br />
        A mechanic, a pawn shop, a potential buyer, or someone who finds your item can all use DataFind
        to check if an item is listed as stolen.
      </Paragraph>

      <Paragraph>
        <Emphasis>Step 4: You get contacted</Emphasis>
        <br />
        Your personal information stays private. Only your chosen contact option is shown to the person
        who may have found your item.
      </Paragraph>

      <Paragraph>
        The result is a system that is simple, effective, and friendly for everyday people.
      </Paragraph>

      <Paragraph>
        All of this is powered by the technology and vision of <Emphasis>Afrotech Group</Emphasis>.
      </Paragraph>

      <Subheading>Built for Real South Africans</Subheading>

      <Paragraph>
        DataFind is not a big corporate product and it was not built for investors or profit. It was
        created by Afrotech Group to solve a real problem that affects real people:
      </Paragraph>

      <List>
        <ListItem>People losing their cars.</ListItem>
        <ListItem>People losing their work tools.</ListItem>
        <ListItem>People losing the only laptop they use for school.</ListItem>
        <ListItem>People losing memories stored on stolen devices.</ListItem>
      </List>

      <Paragraph>
        <Emphasis>DataFind is a way to fight back together.</Emphasis>
      </Paragraph>

      <Subheading>Why Communities Are Embracing It</Subheading>

      <Paragraph>
        Communities are embracing DataFind because it restores something that everyone needs: hope.
      </Paragraph>

      <List>
        <ListItem>Hope that stolen items can be identified.</ListItem>
        <ListItem>Hope that someone honest will check a serial number.</ListItem>
        <ListItem>Hope that recovery is possible.</ListItem>
        <ListItem>Hope that people can protect each other.</ListItem>
      </List>

      <Paragraph>
        When people register their assets and when businesses routinely verify serial numbers, a
        protective network forms that makes it harder for criminals to operate.
      </Paragraph>

      <Subheading>Join the Movement</Subheading>

      <Paragraph>
        South Africans are signing up every day because DataFind is becoming more than an app. It is
        becoming a community safety tool, proudly developed by <Emphasis>Afrotech Group</Emphasis>.
      </Paragraph>

      <Paragraph>
        If you want to protect your assets or help someone else recover theirs, you can join here:
      </Paragraph>

      <HighlightLinkButton
        href="https://www.datafind.co.za"
        target="_blank"
        rel="noopener noreferrer"
      >
        Visit DataFind (www.datafind.co.za)
      </HighlightLinkButton>

      <Paragraph>Your belongings matter.</Paragraph>
      <Paragraph>Your effort matters.</Paragraph>
      <Paragraph>Your security matters.</Paragraph>

      <Paragraph>
        There is finally a platform built to help you keep what is yours: DataFind by Afrotech Group.
      </Paragraph>
    </>
  );

  // SA1stWorkers article (id: 2)
  const sa1stWorkersContent = (
    <>
      <ArticleMeta>
        July 3, 2026 · Afrotech Group · Product Announcement
      </ArticleMeta>

      <ArticleHeading>
        Introducing SA1stWorkers: Connecting South Africans with Trusted Local Professionals
      </ArticleHeading>

      <Paragraph>
        Finding a reliable plumber, electrician, carpenter, cleaner, or handyman shouldn't be difficult. Yet many South Africans still rely on word-of-mouth recommendations or social media posts, often with little way to verify the quality or credibility of the person they're hiring.
      </Paragraph>

      <Paragraph>
        At Afrotech Group, we believe technology can help bridge this gap. That's why we're developing <Emphasis>SA1stWorkers</Emphasis>—a platform designed to connect customers with trusted local professionals while helping skilled workers grow their businesses.
      </Paragraph>

      <Subheading>Why We're Building SA1stWorkers</Subheading>

      <Paragraph>
        Across South Africa, thousands of talented professionals provide exceptional services every day. Unfortunately, many struggle to reach new customers because they don't have websites, marketing budgets, or the tools needed to establish a strong online presence.
      </Paragraph>

      <Paragraph>
        At the same time, customers often face uncertainty when hiring someone they have never worked with before.
      </Paragraph>

      <Paragraph>
        SA1stWorkers is being built to solve both challenges by creating a platform focused on trust, accessibility, and opportunity.
      </Paragraph>

      <Subheading>Empowering Local Professionals</Subheading>

      <Paragraph>
        Our goal is to help independent professionals and small businesses showcase their skills, build credibility, and connect with more clients.
      </Paragraph>

      <Paragraph>
        Whether you're an electrician, plumber, painter, gardener, mechanic, CCTV installer, cleaner, or another skilled tradesperson, SA1stWorkers aims to provide the tools needed to grow your business.
      </Paragraph>

      <Paragraph>
        We're especially passionate about supporting South African entrepreneurs who have the expertise but lack access to digital platforms that help them compete.
      </Paragraph>

      <Subheading>Building Trust Through Technology</Subheading>

      <Paragraph>
        Trust is one of the biggest challenges in the service industry.
      </Paragraph>

      <Paragraph>
        SA1stWorkers is being designed with verification and transparency in mind, helping customers make more informed hiring decisions while giving professionals an opportunity to demonstrate their credibility.
      </Paragraph>

      <Paragraph>
        By combining modern technology with practical business tools, we aim to create a safer and more reliable experience for everyone using the platform.
      </Paragraph>

      <Subheading>More Than a Marketplace</Subheading>

      <Paragraph>
        SA1stWorkers is more than a directory of service providers. Our vision is to build a digital ecosystem that supports local businesses throughout their growth journey.
      </Paragraph>

      <Paragraph>
        From creating professional online profiles to helping businesses establish a stronger digital presence, we want to make technology accessible to every entrepreneur—whether they're just starting out or already serving hundreds of customers.
      </Paragraph>

      <Subheading>Proudly South African</Subheading>

      <Paragraph>
        South Africa is filled with skilled professionals who deserve better opportunities.
      </Paragraph>

      <Paragraph>
        By connecting customers with trusted local workers, we hope to contribute to stronger communities, support small businesses, and create more economic opportunities across the country.
      </Paragraph>

      <Subheading>Coming Soon</Subheading>

      <Paragraph>
        We're working hard to bring SA1stWorkers to life and can't wait to share it with you.
      </Paragraph>

      <Paragraph>
        If you're a skilled professional looking to expand your business, or a customer searching for reliable local services, we'd love to have you join us on this journey.
      </Paragraph>

      <Paragraph>
        The future of finding trusted local professionals starts here.
      </Paragraph>

      <Paragraph>
        <Emphasis>SA1stWorkers — Connecting Skills. Building Trust. Empowering South Africa.</Emphasis>
      </Paragraph>

      <HighlightLinkButton
        href="/products"
      >
        Learn More About Our Products
      </HighlightLinkButton>
    </>
  );

  // Get content based on ID
  const getContent = () => {
    if (id === '1') {
      return {
        title: 'The New Crime-Fighting Platform That Is Spreading Fast: DataFind',
        content: dataFindContent
      };
    } else if (id === '2') {
      return {
        title: 'Introducing SA1stWorkers: Connecting South Africans with Trusted Local Professionals',
        content: sa1stWorkersContent
      };
    }
    return null;
  };

  const articleData = getContent();

  if (!articleData) {
    return (
      <ArticleSection>
        <ArticleWrapper>
          <ArticleContent>
            <BackLink to="/blog">
              <BackIcon />
              Back to blog
            </BackLink>
            <ArticleHeading>Article coming soon</ArticleHeading>
            <Paragraph>
              This article is not available yet. Please check back soon for more stories from Afrotech Group.
            </Paragraph>
          </ArticleContent>
        </ArticleWrapper>
      </ArticleSection>
    );
  }

  return (
    <>
      <PageHeader>
        <PageTitle>{articleData.title}</PageTitle>
      </PageHeader>

      <ArticleSection>
        <ArticleWrapper>
          <ArticleContent>
            <BackLink to="/blog">
              <BackIcon />
              Back to blog
            </BackLink>

            {articleData.content}
          </ArticleContent>
        </ArticleWrapper>
      </ArticleSection>
    </>
  );
};

export default BlogPost;
